import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Case, Switch, When } from 'react-if'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { GiftBowIcon } from '@/atoms/Icons/GiftBowIcon'
import { GiftIcon } from '@/atoms/Icons/GiftIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { TitleXXS } from '@/atoms/Text'
import { useSaleBanner } from '@/services/ContentfulSaleService/hooks'
import { useSafeTrack } from '@/utils/analytics'
import { useCountDown } from '@/utils/countdown/useCountdownHook'
import { getObjectFromLocalStorage, SALE_BANNER_DISMISSED, writeToLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

interface DismissedSales {
  [key: string]: boolean
}

interface SaleBannerProps {
  position?: 'top'
  saleNameOverride?: string | null
}

const GUILD_FUNNEL_PATH_REGEXES = [/\/guild.*/]

export const SaleBanner: React.FC<SaleBannerProps> = ({ position, saleNameOverride }) => {
  const track = useSafeTrack()
  const { saleWithBanner, loading } = useSaleBanner()
  const router = useSafeRouter()
  const [isDismissed, setIsDismissed] = useState(false)
  const [isDashHidden, setIsDashHidden] = useState(false)
  const [wrapContainer, setWrapContainer] = useState<HTMLDivElement | null>(null)
  const wrapRef = useCallback((node: HTMLDivElement | null) => setWrapContainer(node), [])
  const isGuildFunnelPath = GUILD_FUNNEL_PATH_REGEXES.some((r) => r.test(router.asPath))
  const isTop = position === 'top'

  const saleId = saleWithBanner?.id ?? ''

  useEffect(() => {
    if (!saleId) {
      setIsDismissed(false)
      return
    }

    const dismissedSales = getObjectFromLocalStorage<DismissedSales>(SALE_BANNER_DISMISSED) ?? {}
    setIsDismissed(Boolean(dismissedSales[saleId]))
  }, [saleId])

  useEffect(() => {
    if (!wrapContainer) return

    // Track the container width at which we last hid the dash.
    // Only re-show the dash when the container grows significantly wider,
    // preventing layout oscillation at the exact tipping point.
    let lastHiddenWidth = 0

    const checkWrap = () => {
      const children = wrapContainer.children
      if (children.length < 2) return

      const first = children[0] as HTMLElement
      const last = children[children.length - 1] as HTMLElement
      const containerWidth = wrapContainer.clientWidth

      if (first.offsetTop !== last.offsetTop) {
        setIsDashHidden(true)
        lastHiddenWidth = containerWidth
      } else if (containerWidth > lastHiddenWidth + 30) {
        setIsDashHidden(false)
      }
    }

    checkWrap()
    const observer = new ResizeObserver(checkWrap)
    observer.observe(wrapContainer)
    return () => observer.disconnect()
  }, [wrapContainer])

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      track('Sale Banner Clicked', { saleWithBanner })

      const planSelectionElement = document.getElementById('plan-selection')

      // If plan-selection exists, scroll to it, otherwise, let the href handle navigation
      if (planSelectionElement) {
        e.preventDefault()
        planSelectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    [track, saleWithBanner],
  )

  const handleCloseBanner = useCallback(() => {
    if (!saleId) return

    track('Sale Banner Dismissed', { saleId, saleWithBanner })

    const dismissedSales = getObjectFromLocalStorage<DismissedSales>(SALE_BANNER_DISMISSED) ?? {}

    if (!dismissedSales[saleId]) {
      dismissedSales[saleId] = true
      writeToLocalStorage(SALE_BANNER_DISMISSED, dismissedSales)
    }

    setIsDismissed(true)
  }, [saleId, saleWithBanner, track])

  const isExpired = useMemo(() => {
    if (!saleWithBanner?.endDate) return false
    const endDate = new Date(saleWithBanner.endDate)
    return Number.isFinite(endDate.valueOf()) && endDate < new Date()
  }, [saleWithBanner?.endDate])

  if (!saleWithBanner || isDismissed || !saleWithBanner.saleBannerUrl || isExpired || loading) return null

  const color = 'black'

  return (
    <div className={classNames('w-full', isTop ? 'relative z-40' : 'absolute top-[62px] z-40 md:top-16')}>
      <div className="flex w-full items-center justify-between bg-white shadow-sm">
        <div className="hidden w-11 md:block">
          {/* This div is here to keep the space on left and right equal so the middle content is centered on desktop. */}
        </div>
        <div className="grow-1">
          <InternalLink href={saleWithBanner?.saleBannerUrl} onClick={handleClick}>
            <div ref={wrapRef} className="flex flex-wrap items-center justify-center gap-x-1 px-3 py-2">
              <div className="flex items-center gap-1">
                <Switch>
                  <Case condition={saleWithBanner?.saleBannerIcon === 'gift'}>
                    <GiftIcon className="shrink-0" size={20} color={color} />
                  </Case>
                  <Case condition={saleWithBanner?.saleBannerIcon === 'giftBow'}>
                    <GiftBowIcon className="shrink-0" size={20} color={color} />
                  </Case>
                </Switch>
                <TitleXXS color={color} weight="semibold">
                  {saleNameOverride ?? saleWithBanner.saleBannerText}
                  {!!saleWithBanner.showSaleBannerCountdown && (
                    <span className={isDashHidden ? 'hidden' : undefined}> —</span>
                  )}
                </TitleXXS>
              </div>
              {!!saleWithBanner.showSaleBannerCountdown && <Countdown endDate={saleWithBanner?.endDate} />}
            </div>
          </InternalLink>
        </div>
        <When condition={!isGuildFunnelPath}>
          <div className="w-11 self-stretch">
            <Button className="h-full w-full" variant="plain" onClick={handleCloseBanner}>
              <CloseIcon size={20} color={color} />
            </Button>
          </div>
        </When>
      </div>
    </div>
  )
}

const Countdown: React.FC<{ endDate: string | undefined }> = ({ endDate }) => {
  const format = useMemo(
    () => ({
      listStyle: 'narrow' as const,
      unitDisplay: 'long' as const,
    }),
    [],
  )

  const countdown = useCountDown({
    endDate: endDate,
    format,
    numUnits: 5,
  })

  return (
    <TitleXXS className="text-nowrap" color="black" weight="semibold">
      {countdown?.formattedCountdown}
    </TitleXXS>
  )
}
